import axios, { AxiosError } from 'axios';
import type { InternalAxiosRequestConfig, AxiosResponse } from 'axios';
import toast from 'react-hot-toast';
import { getAuthToken, removeAuthToken, refreshAuthSession, ensureFreshSession, buildLoginRedirectUrl } from '../helper/helper';

interface ErrorResponseData {
  message?: string;
  code?: number;
}

interface RetriableRequestConfig extends InternalAxiosRequestConfig {
  _retriedAfterRefresh?: boolean;
}
const APP_PUBLIC_URL = import.meta.env.BASE_URL || "/";
const GUEST_ACCESS_TOKEN = (import.meta.env.VITE_GUEST_ACCESS_TOKEN || "").trim();

export const Api = axios.create({
  baseURL: import.meta.env.VITE_API_URL,
});

Api.interceptors.request.use(
  async (config: InternalAxiosRequestConfig) => {
    config.headers = config.headers || {};

    let token = getAuthToken();
    // Renew an expired / about-to-expire token before sending, instead of sending a request the backend
    // will treat as anonymous (it answers 403, which the 401 handling below never sees). If the session
    // is dead this also clears it, so the request goes out as the guest it now is.
    if (token) token = (await ensureFreshSession()) ?? getAuthToken();

    // Prefer logged-in token. If not present, optionally use a configured guest token
    // to fetch protected read data without forcing user login.
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    } else if (GUEST_ACCESS_TOKEN) {
      config.headers.Authorization = `Bearer ${GUEST_ACCESS_TOKEN}`;
    } else {
      delete config.headers.Authorization;
    }

    return config;
  },
  (error: AxiosError) => {
    console.error("Request error:", error);
    return Promise.reject(error);
  }
);

/**
 * Forces a fresh login: clears the session, tells the user why, and sends them to /login carrying
 * where they were as a `redirect` param so they land back there once they re-authenticate (e.g. a
 * waiver reminder email link opened with a since-expired session).
 */
export const expireSession = () => {
  removeAuthToken();
  toast.error('Session Expired. Please login again.');
  const base = APP_PUBLIC_URL.endsWith('/') ? APP_PUBLIC_URL.slice(0, -1) : APP_PUBLIC_URL;
  const currentPath = `${window.location.pathname}${window.location.search}`;
  // Already mid-auth-flow (login/signup/otp all live on this one route) — don't wrap it in
  // another redirect layer, just reload it as-is.
  const alreadyOnLogin = window.location.pathname === `${base}/login`;
  window.location.href = alreadyOnLogin ? currentPath : `${base}${buildLoginRedirectUrl(currentPath)}`;
};

Api.interceptors.response.use(
  (response: AxiosResponse) => {
    return response;
  },
  async (error: AxiosError<ErrorResponseData>) => {
    if (error.response) {
      const responseData = error.response.data;
      const hasToken = !!getAuthToken();

      const isAuthError =
        responseData?.message === "Invalid or expired token" ||
        responseData?.code === 401 ||
        responseData?.message === "User not found";

      if (isAuthError) {
        // Guest / never logged in — let the request fail naturally, no session-expired messaging.
        if (!hasToken) {
          return Promise.reject(error);
        }

        const originalRequest = error.config as RetriableRequestConfig | undefined;
        // "User not found" means the account itself is gone — refreshing the token won't help.
        const canRetryWithRefresh =
          responseData?.message !== "User not found" &&
          originalRequest &&
          !originalRequest._retriedAfterRefresh;

        if (canRetryWithRefresh) {
          const newAccessToken = await refreshAuthSession();
          if (newAccessToken) {
            originalRequest._retriedAfterRefresh = true;
            originalRequest.headers.Authorization = `Bearer ${newAccessToken}`;
            return Api(originalRequest);
          }
        }

        // Either the refresh token has also expired, or refresh wasn't attempted — the
        // user was logged in (hasToken) so tell them their session ended.
        expireSession();
        return Promise.reject(error);
      }
      else {
        // toast.error(responseData?.message || "An error occurred");

        // Server returned a response with an error status code
        console.error(
          `API Error: ${error.response.status} - ${responseData?.message || error.response.statusText}`
        );
        return Promise.reject(error);
      }
    } else if (error.request) {
      // No response received from the server
      console.error("No response received:", error.request);
    } else {
      // Something else caused the error
      console.error("Error:", error.message);
    }

    // toast.error(error.message || "An unexpected error occurred");
    // Reject the promise to allow custom error handling elsewhere
    return Promise.reject(error);
  }
);